import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, ArrowRight, Check, Plus, X, Camera, Video, 
  User, Briefcase, Award, Share2, Loader2, Upload, Search, Sparkles
} from "lucide-react";
import AIProfileBuilder from "@/components/AIProfileBuilder";
import { ProfileEditHints } from "@/components/OnboardingHints";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Physical attribute categories that show height/weight/etc
const PHYSICAL_ATTRIBUTE_CATEGORIES = ["Actor", "Model", "Dancer", "Stunt Performer"];

const WIZARD_STEPS = [
  { id: 1, title: "Basics", icon: User, description: "Name, city, category" },
  { id: 2, title: "Portfolio", icon: Camera, description: "Photos & showreel" },
  { id: 3, title: "Work", icon: Briefcase, description: "Experience & credits" },
  { id: 4, title: "Awards", icon: Award, description: "Achievements" },
  { id: 5, title: "Review", icon: Check, description: "Final check" },
];

// Searchable Select Component with Add New
function SearchableSelect({ 
  label, 
  options, 
  value, 
  onChange, 
  placeholder,
  allowAdd = false,
  taxonomyType,
  required = false 
}) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const wrapperRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddNew = async () => {
    if (!search.trim()) return;
    setAdding(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/taxonomy/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ type: taxonomyType, value: search.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        onChange(search.trim());
        toast.success(`Added "${search.trim()}"`);
        setIsOpen(false);
        setSearch("");
      } else {
        toast.error(data.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add new option");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-1.5" ref={wrapperRef}>
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      <div className="relative">
        <div 
          className="flex items-center border rounded-lg px-3 py-2.5 cursor-pointer bg-white hover:border-violet-300"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Search className="w-4 h-4 text-gray-400 mr-2" />
          <input
            type="text"
            value={isOpen ? search : value}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            placeholder={placeholder}
            className="flex-1 outline-none bg-transparent text-sm"
            onFocus={() => setIsOpen(true)}
          />
          {value && !isOpen && (
            <X 
              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" 
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
            />
          )}
        </div>
        
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => (
                <div
                  key={i}
                  className={`px-3 py-2 cursor-pointer hover:bg-violet-50 text-sm ${
                    opt === value ? "bg-violet-100 text-violet-700" : ""
                  }`}
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                    setSearch("");
                  }}
                >
                  {opt}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">No matches found</div>
            )}
            
            {allowAdd && search.trim() && !options.includes(search.trim()) && (
              <div
                className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-green-600 border-t flex items-center gap-2"
                onClick={handleAddNew}
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add "{search.trim()}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Multi-select with chips
function MultiSelectChips({ 
  label, 
  options, 
  selected, 
  onChange, 
  placeholder,
  allowAdd = false,
  taxonomyType,
  max = 20 
}) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const wrapperRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase()) && !selected.includes(opt)
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAdd = (value) => {
    if (selected.length >= max) {
      toast.error(`Maximum ${max} items allowed`);
      return;
    }
    onChange([...selected, value]);
    setSearch("");
  };

  const handleRemove = (value) => {
    onChange(selected.filter(s => s !== value));
  };

  const handleAddNew = async () => {
    if (!search.trim()) return;
    setAdding(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/taxonomy/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ type: taxonomyType, value: search.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        handleAdd(search.trim());
        toast.success(`Added "${search.trim()}"`);
      } else {
        toast.error(data.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add new option");
    } finally {
      setAdding(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="space-y-1.5" ref={wrapperRef}>
      <Label>{label}</Label>
      
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((item, i) => (
            <Badge key={i} variant="secondary" className="gap-1 pr-1">
              {item}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-red-500" 
                onClick={() => handleRemove(item)}
              />
            </Badge>
          ))}
        </div>
      )}
      
      {/* Search input */}
      <div className="relative">
        <div 
          className="flex items-center border rounded-lg px-3 py-2.5 bg-white hover:border-violet-300"
        >
          <Search className="w-4 h-4 text-gray-400 mr-2" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            placeholder={placeholder}
            className="flex-1 outline-none bg-transparent text-sm"
            onFocus={() => setIsOpen(true)}
          />
        </div>
        
        {isOpen && (search || filteredOptions.length > 0) && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filteredOptions.slice(0, 20).map((opt, i) => (
              <div
                key={i}
                className="px-3 py-2 cursor-pointer hover:bg-violet-50 text-sm"
                onClick={() => {
                  handleAdd(opt);
                  setIsOpen(false);
                }}
              >
                {opt}
              </div>
            ))}
            
            {allowAdd && search.trim() && !options.includes(search.trim()) && !selected.includes(search.trim()) && (
              <div
                className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-green-600 border-t flex items-center gap-2"
                onClick={handleAddNew}
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add "{search.trim()}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TalentWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState({ categories: [], skills: [], cities: [] });
  const [uploading, setUploading] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    // Step 1: Basics
    full_name: "",
    display_name: "",
    headline: "",
    city: "",
    primary_category: "",
    secondary_categories: [],
    skills: [],
    // Physical attributes (only for Actor/Model)
    gender: "",
    date_of_birth: "",
    height_cm: "",
    weight_kg: "",
    body_type: "",
    skin_tone: "",
    eye_color: "",
    hair_color: "",
    
    // Step 2: Portfolio
    profile_photo: "",
    additional_photos: [],
    showreel_url: "",
    intro_video: "",
    audio_samples: [],
    
    // Step 3: Work
    bio: "",
    years_of_experience: "",
    credits: [],
    
    // Step 4: Awards
    awards: [],
    trainings: [],
    
    // Step 5: Review (social links)
    social_links: [],
    terms_accepted: false
  });

  useEffect(() => {
    fetchTaxonomy();
    fetchExistingProfile();
  }, []);

  const fetchTaxonomy = async () => {
    try {
      const response = await fetch(`${API_URL}/api/taxonomy`);
      if (response.ok) {
        const data = await response.json();
        const grouped = data.grouped || {};
        setTaxonomy({
          categories: (grouped.category || []).map(c => c.value),
          skills: (grouped.skill || []).map(s => s.value),
          cities: (grouped.city || []).map(c => c.value)
        });
      }
    } catch (error) {
      console.error("Error fetching taxonomy:", error);
    }
  };

  const fetchExistingProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.profile_id) {
          // Convert social_presence individual fields to array format for form
          const socialLinks = [];
          const sp = data.social_presence || {};
          if (sp.instagram) socialLinks.push({ platform: "Instagram", url: sp.instagram });
          if (sp.facebook) socialLinks.push({ platform: "Facebook", url: sp.facebook });
          if (sp.facebook_page) socialLinks.push({ platform: "Facebook Page", url: sp.facebook_page });
          if (sp.youtube) socialLinks.push({ platform: "YouTube", url: sp.youtube });
          if (sp.linkedin) socialLinks.push({ platform: "LinkedIn", url: sp.linkedin });
          if (sp.twitter) socialLinks.push({ platform: "Twitter/X", url: sp.twitter });
          if (sp.imdb) socialLinks.push({ platform: "IMDb", url: sp.imdb });
          if (sp.personal_website) socialLinks.push({ platform: "Website", url: sp.personal_website });
          
          // Populate form with existing data
          setFormData(prev => ({
            ...prev,
            full_name: data.personal_info?.full_name || "",
            display_name: data.personal_info?.display_name || "",
            headline: data.professional_info?.headline || "",
            city: data.personal_info?.city || "",
            primary_category: data.professional_info?.primary_category || "",
            secondary_categories: data.professional_info?.secondary_categories || [],
            skills: data.professional_info?.skills || [],
            gender: data.personal_info?.gender || "",
            date_of_birth: data.personal_info?.date_of_birth || "",
            height_cm: data.personal_info?.height_cm || "",
            weight_kg: data.personal_info?.weight_kg || "",
            body_type: data.personal_info?.body_type || "",
            skin_tone: data.personal_info?.skin_tone || "",
            eye_color: data.personal_info?.eye_color || "",
            hair_color: data.personal_info?.hair_color || "",
            profile_photo: data.portfolio?.profile_photo || "",
            additional_photos: data.portfolio?.additional_photos || [],
            showreel_url: data.portfolio?.showreel_url || "",
            intro_video: data.portfolio?.intro_video || "",
            audio_samples: data.portfolio?.audio_samples || [],
            bio: data.professional_info?.bio || "",
            years_of_experience: data.professional_info?.years_of_experience || "",
            credits: data.professional_info?.credits || [],
            awards: data.professional_info?.awards || [],
            trainings: data.professional_info?.trainings || [],
            social_links: socialLinks,
            terms_accepted: data.preferences?.terms_accepted || false
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        personal_info: {
          full_name: formData.full_name,
          display_name: formData.display_name || formData.full_name.split(" ")[0],
          city: formData.city,
          country: "India",
          gender: formData.gender,
          date_of_birth: formData.date_of_birth,
          height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
          weight_kg: formData.weight_kg ? parseInt(formData.weight_kg) : null,
          body_type: formData.body_type,
          skin_tone: formData.skin_tone,
          eye_color: formData.eye_color,
          hair_color: formData.hair_color
        },
        professional_info: {
          headline: formData.headline,
          primary_category: formData.primary_category,
          secondary_categories: formData.secondary_categories,
          skills: formData.skills,
          bio: formData.bio,
          years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
          credits: formData.credits,
          awards: formData.awards,
          trainings: formData.trainings
        },
        portfolio: {
          profile_photo: formData.profile_photo,
          additional_photos: formData.additional_photos,
          showreel_url: formData.showreel_url,
          intro_video: formData.intro_video,
          audio_samples: formData.audio_samples
        },
        social_presence: {
          // Convert social_links array to individual fields
          instagram: formData.social_links.find(l => l.platform === "Instagram")?.url || null,
          facebook: formData.social_links.find(l => l.platform === "Facebook")?.url || null,
          facebook_page: formData.social_links.find(l => l.platform === "Facebook Page")?.url || null,
          youtube: formData.social_links.find(l => l.platform === "YouTube")?.url || null,
          linkedin: formData.social_links.find(l => l.platform === "LinkedIn")?.url || null,
          twitter: formData.social_links.find(l => l.platform === "Twitter/X")?.url || null,
          imdb: formData.social_links.find(l => l.platform === "IMDb")?.url || null,
          personal_website: formData.social_links.find(l => l.platform === "Website")?.url || null
        },
        preferences: {
          terms_accepted: formData.terms_accepted
        },
        is_draft: true
      };

      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Draft saved");
      }
    } catch (error) {
      console.error("Error saving draft:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Save draft automatically
    await saveDraft();
    if (currentStep < WIZARD_STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle AI Profile Builder output
  const handleAIApply = (aiContent) => {
    setFormData(prev => ({
      ...prev,
      bio: aiContent.bio || prev.bio,
      headline: aiContent.headline || prev.headline,
      skills: aiContent.skills && aiContent.skills.length > 0 
        ? [...new Set([...prev.skills, ...aiContent.skills])] // Merge skills, remove duplicates
        : prev.skills,
      primary_category: aiContent.category || prev.primary_category
    }));
    // Move to work step if bio was generated (it's in step 3)
    if (aiContent.bio && currentStep === 1) {
      toast.info("AI content applied! Continue filling other details or jump to Work step to review your bio.");
    }
  };

  const handleSubmit = async () => {
    if (!formData.full_name || !formData.primary_category) {
      toast.error("Please fill in Name and Category");
      return;
    }
    if (!formData.terms_accepted) {
      toast.error("Please accept Terms & Conditions");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        personal_info: {
          full_name: formData.full_name,
          display_name: formData.display_name || formData.full_name.split(" ")[0],
          city: formData.city,
          country: "India",
          gender: formData.gender,
          date_of_birth: formData.date_of_birth,
          height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
          weight_kg: formData.weight_kg ? parseInt(formData.weight_kg) : null,
          body_type: formData.body_type,
          skin_tone: formData.skin_tone,
          eye_color: formData.eye_color,
          hair_color: formData.hair_color
        },
        professional_info: {
          headline: formData.headline,
          primary_category: formData.primary_category,
          secondary_categories: formData.secondary_categories,
          skills: formData.skills,
          bio: formData.bio,
          years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
          credits: formData.credits,
          awards: formData.awards,
          trainings: formData.trainings
        },
        portfolio: {
          profile_photo: formData.profile_photo,
          additional_photos: formData.additional_photos,
          showreel_url: formData.showreel_url,
          intro_video: formData.intro_video,
          audio_samples: formData.audio_samples
        },
        social_presence: {
          // Convert social_links array to individual fields
          instagram: formData.social_links.find(l => l.platform === "Instagram")?.url || null,
          facebook: formData.social_links.find(l => l.platform === "Facebook")?.url || null,
          facebook_page: formData.social_links.find(l => l.platform === "Facebook Page")?.url || null,
          youtube: formData.social_links.find(l => l.platform === "YouTube")?.url || null,
          linkedin: formData.social_links.find(l => l.platform === "LinkedIn")?.url || null,
          twitter: formData.social_links.find(l => l.platform === "Twitter/X")?.url || null,
          imdb: formData.social_links.find(l => l.platform === "IMDb")?.url || null,
          personal_website: formData.social_links.find(l => l.platform === "Website")?.url || null
        },
        preferences: {
          terms_accepted: formData.terms_accepted
        },
        is_draft: false
      };

      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Profile published!");
        navigate("/talent/dashboard");
      } else {
        const err = await response.json();
        throw new Error(err.detail || "Failed to save");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    setUploading(true);
    
    try {
      const token = localStorage.getItem("token");
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("file_type", type === "photo" ? "image" : "video");

      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        if (type === "photo") {
          updateField("profile_photo", data.url);
        } else if (type === "video") {
          updateField("intro_video", data.url);
        }
        toast.success("Uploaded successfully");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const showPhysicalAttributes = PHYSICAL_ATTRIBUTE_CATEGORIES.includes(formData.primary_category);
  const progress = (currentStep / WIZARD_STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-lg font-semibold">Create Talent Profile</h1>
            <Button variant="outline" onClick={saveDraft} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Draft"}
            </Button>
          </div>
          
          {/* Progress */}
          <Progress value={progress} className="h-2 mb-4" />
          
          {/* Steps */}
          <div className="flex justify-between">
            {WIZARD_STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              return (
                <div 
                  key={step.id}
                  className={`flex flex-col items-center cursor-pointer ${
                    isActive ? "text-violet-600" : isCompleted ? "text-green-600" : "text-gray-400"
                  }`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                    isActive ? "bg-violet-100" : isCompleted ? "bg-green-100" : "bg-gray-100"
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Basics */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Basic Information</h2>
                  <Button 
                    variant="outline"
                    onClick={() => setShowAIBuilder(true)}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="build-with-ai-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Build with AI
                  </Button>
                </div>
                
                {/* Onboarding Hint for first-time users */}
                <ProfileEditHints 
                  onAIClick={() => setShowAIBuilder(true)}
                  onPhotoClick={() => setCurrentStep(2)}
                  onPublishClick={() => setCurrentStep(5)}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => updateField("full_name", e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label>Display Name / Stage Name</Label>
                    <Input
                      value={formData.display_name}
                      onChange={(e) => updateField("display_name", e.target.value)}
                      placeholder="How you want to be known"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Headline</Label>
                  <Input
                    value={formData.headline}
                    onChange={(e) => updateField("headline", e.target.value)}
                    placeholder="e.g., Actor | Model | Singer with 5+ years experience"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SearchableSelect
                    label="City"
                    options={taxonomy.cities}
                    value={formData.city}
                    onChange={(v) => updateField("city", v)}
                    placeholder="Select your city"
                    allowAdd
                    taxonomyType="city"
                  />
                  
                  <SearchableSelect
                    label="Primary Category"
                    options={taxonomy.categories}
                    value={formData.primary_category}
                    onChange={(v) => updateField("primary_category", v)}
                    placeholder="Select category"
                    allowAdd
                    taxonomyType="category"
                    required
                  />
                </div>

                <MultiSelectChips
                  label="Skills"
                  options={taxonomy.skills}
                  selected={formData.skills}
                  onChange={(v) => updateField("skills", v)}
                  placeholder="Search and add skills..."
                  allowAdd
                  taxonomyType="skill"
                  max={15}
                />

                {/* Physical Attributes - Only for Actor/Model */}
                {showPhysicalAttributes && (
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-medium mb-4">Physical Attributes (Optional)</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      These help casting directors find the right fit for roles.
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-1.5">
                        <Label>Gender</Label>
                        <select
                          value={formData.gender}
                          onChange={(e) => updateField("gender", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Non-binary">Non-binary</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => updateField("date_of_birth", e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Height (cm)</Label>
                        <Input
                          type="number"
                          value={formData.height_cm}
                          onChange={(e) => updateField("height_cm", e.target.value)}
                          placeholder="170"
                        />
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Weight (kg)</Label>
                        <Input
                          type="number"
                          value={formData.weight_kg}
                          onChange={(e) => updateField("weight_kg", e.target.value)}
                          placeholder="65"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div className="space-y-1.5">
                        <Label>Body Type</Label>
                        <select
                          value={formData.body_type}
                          onChange={(e) => updateField("body_type", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Slim">Slim</option>
                          <option value="Athletic">Athletic</option>
                          <option value="Average">Average</option>
                          <option value="Muscular">Muscular</option>
                          <option value="Heavy">Heavy</option>
                        </select>
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Skin Tone</Label>
                        <select
                          value={formData.skin_tone}
                          onChange={(e) => updateField("skin_tone", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Fair">Fair</option>
                          <option value="Wheatish">Wheatish</option>
                          <option value="Medium">Medium</option>
                          <option value="Olive">Olive</option>
                          <option value="Dark">Dark</option>
                        </select>
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Eye Color</Label>
                        <select
                          value={formData.eye_color}
                          onChange={(e) => updateField("eye_color", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Black">Black</option>
                          <option value="Brown">Brown</option>
                          <option value="Hazel">Hazel</option>
                          <option value="Green">Green</option>
                          <option value="Blue">Blue</option>
                          <option value="Gray">Gray</option>
                        </select>
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label>Hair Color</Label>
                        <select
                          value={formData.hair_color}
                          onChange={(e) => updateField("hair_color", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2.5 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Black">Black</option>
                          <option value="Brown">Brown</option>
                          <option value="Blonde">Blonde</option>
                          <option value="Red">Red</option>
                          <option value="Gray">Gray</option>
                          <option value="White">White</option>
                          <option value="Colored">Colored/Dyed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Portfolio */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Portfolio</h2>
                <p className="text-gray-500">Add photos and videos to showcase your work</p>
                
                {/* Profile Photo */}
                <div className="space-y-2">
                  <Label>Profile Photo</Label>
                  <div className="flex items-start gap-4">
                    {formData.profile_photo ? (
                      <div className="relative">
                        <img 
                          src={formData.profile_photo} 
                          alt="Profile" 
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => updateField("profile_photo", "")}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        className="w-32 h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-violet-400"
                        onClick={() => photoInputRef.current?.click()}
                      >
                        {uploading ? (
                          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-gray-400" />
                            <span className="text-xs text-gray-500 mt-1">Upload</span>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], "photo")}
                    />
                  </div>
                </div>

                {/* Showreel URL */}
                <div className="space-y-1.5">
                  <Label>Showreel / Demo Reel URL</Label>
                  <Input
                    value={formData.showreel_url}
                    onChange={(e) => updateField("showreel_url", e.target.value)}
                    placeholder="YouTube, Vimeo, or other video link"
                  />
                  <p className="text-xs text-gray-500">Link to your best work compilation</p>
                </div>

                {/* Intro Video */}
                <div className="space-y-2">
                  <Label>Introduction Video</Label>
                  {formData.intro_video ? (
                    <div className="relative w-64">
                      <video src={formData.intro_video} className="w-full rounded-lg" controls />
                      <button
                        onClick={() => updateField("intro_video", "")}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="w-64 h-36 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-violet-400"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <Video className="w-8 h-8 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Upload short intro (30-60s)</span>
                    </div>
                  )}
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], "video")}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Work Experience */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Work Experience</h2>
                
                <div className="space-y-1.5">
                  <Label>Bio / About</Label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => updateField("bio", e.target.value)}
                    placeholder="Tell us about yourself, your journey, and what makes you unique..."
                    rows={4}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Years of Experience</Label>
                  <Input
                    type="number"
                    value={formData.years_of_experience}
                    onChange={(e) => updateField("years_of_experience", e.target.value)}
                    placeholder="e.g., 5"
                    className="w-32"
                  />
                </div>

                {/* Credits */}
                <div className="space-y-2">
                  <Label>Credits / Notable Work</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a credit (e.g., Lead role in XYZ Film)"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          updateField("credits", [...formData.credits, e.target.value.trim()]);
                          e.target.value = "";
                        }
                      }}
                    />
                    <Button 
                      variant="outline"
                      onClick={(e) => {
                        const input = e.target.previousSibling;
                        if (input.value.trim()) {
                          updateField("credits", [...formData.credits, input.value.trim()]);
                          input.value = "";
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.credits.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.credits.map((credit, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          {credit}
                          <X 
                            className="w-3 h-3 cursor-pointer" 
                            onClick={() => updateField("credits", formData.credits.filter((_, idx) => idx !== i))}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Awards */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Awards & Achievements</h2>
                
                {/* Awards */}
                <div className="space-y-2">
                  <Label>Awards</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add an award (e.g., Best Actor - XYZ Film Festival 2024)"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          updateField("awards", [...formData.awards, e.target.value.trim()]);
                          e.target.value = "";
                        }
                      }}
                    />
                    <Button variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.awards.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.awards.map((award, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          {award}
                          <X 
                            className="w-3 h-3 cursor-pointer" 
                            onClick={() => updateField("awards", formData.awards.filter((_, idx) => idx !== i))}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Training */}
                <div className="space-y-2">
                  <Label>Training / Education</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add training (e.g., FTII Acting Workshop 2023)"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          updateField("trainings", [...formData.trainings, e.target.value.trim()]);
                          e.target.value = "";
                        }
                      }}
                    />
                    <Button variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.trainings.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.trainings.map((training, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          {training}
                          <X 
                            className="w-3 h-3 cursor-pointer" 
                            onClick={() => updateField("trainings", formData.trainings.filter((_, idx) => idx !== i))}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Review */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Review & Publish</h2>
                
                {/* Social Links */}
                <div className="space-y-2">
                  <Label>Social Media Links</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {["Instagram", "Facebook", "Facebook Page", "YouTube", "LinkedIn", "Twitter/X", "IMDb", "Website"].map((platform) => (
                      <div key={platform} className="flex items-center gap-2">
                        <span className="text-sm w-28 text-gray-600">{platform}</span>
                        <Input
                          placeholder={`${platform} URL`}
                          value={formData.social_links.find(l => l.platform === platform)?.url || ""}
                          onChange={(e) => {
                            const existing = formData.social_links.filter(l => l.platform !== platform);
                            if (e.target.value.trim()) {
                              updateField("social_links", [...existing, { platform, url: e.target.value.trim() }]);
                            } else {
                              updateField("social_links", existing);
                            }
                          }}
                          className="flex-1"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h3 className="font-medium">Profile Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Name:</span> {formData.full_name || "-"}</div>
                    <div><span className="text-gray-500">City:</span> {formData.city || "-"}</div>
                    <div><span className="text-gray-500">Category:</span> {formData.primary_category || "-"}</div>
                    <div><span className="text-gray-500">Skills:</span> {formData.skills.length} added</div>
                    <div><span className="text-gray-500">Credits:</span> {formData.credits.length} added</div>
                    <div><span className="text-gray-500">Awards:</span> {formData.awards.length} added</div>
                  </div>
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <input
                    type="checkbox"
                    checked={formData.terms_accepted}
                    onChange={(e) => updateField("terms_accepted", e.target.checked)}
                    className="mt-1 w-5 h-5"
                  />
                  <div>
                    <Label className="cursor-pointer">
                      I accept the Terms of Service and Privacy Policy *
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">
                      By checking this, you agree to our terms and consent to the processing of your data.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          
          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Publish Profile
            </Button>
          )}
        </div>
      </div>

      {/* AI Profile Builder Modal */}
      <AIProfileBuilder
        isOpen={showAIBuilder}
        onClose={() => setShowAIBuilder(false)}
        profileType="talent"
        onApply={handleAIApply}
        existingData={{
          bio: formData.bio,
          headline: formData.headline,
          skills: formData.skills
        }}
      />
    </div>
  );
}
